import type { WelcomeWeekEffectiveEvent, WelcomeWeekMasterEvent } from '@/types/welcomeWeek';

export function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function fromDateTimeLocal(value: string): string {
  return new Date(value).toISOString();
}

export function formatEventTime(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const sameDay = start.toDateString() === end.toDateString();
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const startLabel = `${dateFormatter.format(start)} ${timeFormatter.format(start)}`;
  const endLabel = sameDay ? timeFormatter.format(end) : `${dateFormatter.format(end)} ${timeFormatter.format(end)}`;
  return `${startLabel} - ${endLabel}`;
}

export function formatLocation(event: Pick<WelcomeWeekEffectiveEvent | WelcomeWeekMasterEvent, 'location_name' | 'location_address'>): string {
  return [event.location_name, event.location_address].filter(Boolean).join(', ');
}

// The ?season= parameter is optional everywhere (pages and the backend fall
// back to the current season), so navigation links stay clean by default and
// only propagate a season that was explicitly selected in the URL.
export function seasonQuery(explicitSeason: string | null | undefined, extra?: string): string {
  const parts = [explicitSeason && `season=${explicitSeason}`, extra].filter(Boolean);
  return parts.length ? `?${parts.join('&')}` : '';
}

// Welcome-Week ICS/JSON feeds are served by the CalOne app (v1.calone.com),
// not by this events app. Feed paths in the DB are relative (e.g.
// "/ics/26h/omega/PI-<token>.ics"), so join them to the CalOne host rather than
// window.location.origin (which would be the Lovable/events domain).
const FEEDS_BASE_URL = (
  import.meta.env.VITE_CALONE_FEEDS_BASE_URL || 'https://v1.calone.com'
).replace(/\/$/, '');

export function buildSubscribeUrl(path: string | undefined): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${FEEDS_BASE_URL}${path}`;
}

// The editable/read-only full-week calendar view is rendered by the CalOne app
// (www.calone.com/omega/<slug>/fullview), not by this events app or the v1 feed
// host. The public link is read-only; editing happens behind a pin URL.
export const APP_BASE_URL = (
  import.meta.env.VITE_CALONE_APP_BASE_URL || 'https://www.calone.com'
).replace(/\/$/, '');

export function buildFullviewUrl(slugOrCode: string | undefined, orgSlug?: string): string {
  if (!slugOrCode) return '';
  const org = orgSlug || 'omega';
  return `${APP_BASE_URL}/${org.toLowerCase()}/${slugOrCode.toLowerCase()}/fullview`;
}

// Editable fullview for an authorized admin who holds the group's edit pin.
export function buildFullviewEditUrl(slugOrCode: string | undefined, pin: string | undefined, orgSlug?: string): string {
  if (!slugOrCode || !pin) return '';
  const org = orgSlug || 'omega';
  return `${APP_BASE_URL}/${org.toLowerCase()}/${slugOrCode.toLowerCase()}/${pin}/fullview`;
}

// The pin-gated CalOne calendar JSON the fullview loads (served by the feed
// host with permissive CORS). This is the same source of truth as the ICS feed
// and the editable fullview, so a list-style editor reading it stays in sync.
export function buildWwEditJsonUrl(
  season: string | undefined,
  code: string | undefined,
  pin: string | undefined,
  orgSlug?: string,
): string {
  if (!season || !code || !pin) return '';
  const prefix = `${(orgSlug || 'omega').toLowerCase()}-edit`;
  return `${FEEDS_BASE_URL}/${prefix}/${season}/${code}/${pin}.json`;
}

// Base URL for the pin-gated per-event mutation API (add/edit/delete). Edits
// here update the same CalOne JSON + ICS the fullview uses.
export function buildWwEventApiBase(slugOrCode: string | undefined, pin: string | undefined, orgSlug?: string): string {
  if (!slugOrCode || !pin) return '';
  const org = orgSlug || 'omega';
  return `${APP_BASE_URL}/api/${org.toLowerCase()}/${slugOrCode.toLowerCase()}/${pin}/events`;
}

// The world-readable (CORS-enabled) source calendar JSON the editor reads.
export function buildSourceJsonUrl(season: string | undefined, stem: string): string {
  if (!season || !stem) return '';
  return `${FEEDS_BASE_URL}/static/json/${season}/${stem}.json`;
}

// CalOne's editable Toast-UI full-week view for a source calendar.
export function buildSourceFullviewUrl(username: string): string {
  if (!username) return '';
  return `${APP_BASE_URL}/${username.toLowerCase()}/fullview`;
}

// Whole-document save endpoint (CORS-enabled). Saving a source calendar here
// triggers CalOne's rebuild of the merged Omega master.
export const SCHEDULESLIST_URL = `${FEEDS_BASE_URL}/api/scheduleslist`;

export function eventDescription(event: Pick<WelcomeWeekEffectiveEvent, 'description' | 'notes'>): string {
  return [event.description, event.notes].filter(Boolean).join('\n\n');
}

export function labelFromLocationHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '').trim();
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').trim();
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const LOCATION_URL_RE = /\bhttps?:\/\/[^\s]+/i;

// When the typed location contains a URL (e.g. "Vrimle https://link.mazemap.com/4Ou9YlKE"),
// the leading text becomes the link's label and we link straight to that URL —
// no "• MazeMap" suffix. With no URL we fall back to a Google Maps search link.
export function locationHtmlFromLabel(label: string): string {
  const t = label.trim();
  if (!t) return '';
  const urlMatch = t.match(LOCATION_URL_RE);
  if (urlMatch) {
    const url = urlMatch[0];
    const labelText = t.slice(0, urlMatch.index).trim() || t.slice(urlMatch.index! + url.length).trim() || 'Kart';
    return `<a href="${escapeHtml(url)}" rel="noopener noreferrer" target="_blank">${escapeHtml(labelText)}</a>`;
  }
  const enc = encodeURIComponent(t);
  return `<a href="https://www.google.com/maps/search/${enc}" rel="noopener noreferrer" target="_blank">${escapeHtml(t)}</a>`;
}

// Build sanitizable HTML for a feed event's location. The CalOne master often
// stores the location as a ready-made <a> anchor in location_name (the fullview
// and the admin editor render it as HTML), so the public list must render it as
// HTML too — otherwise React escapes the tag and the card shows raw
// "<a href=...>loftet sit</a>" text instead of a clickable link. When a
// structured location_url is present we build a clean link from it instead.
export function locationDisplayHtml(
  event: Pick<WelcomeWeekEffectiveEvent | WelcomeWeekMasterEvent, 'location_name' | 'location_address' | 'location_url'>
): string {
  const name = (event.location_name || '').trim();
  const address = (event.location_address || '').trim();
  const url = (event.location_url || '').trim();
  if (url) {
    const label = labelFromLocationHtml(name) || name || address || 'Kart';
    const link = `<a href="${escapeHtml(url)}" rel="noopener noreferrer" target="_blank">${escapeHtml(label)}</a>`;
    return address && address !== label ? `${link}, ${escapeHtml(address)}` : link;
  }
  // No structured URL: location_name may already be an <a> anchor (rendered by
  // the sanitizer) or plain text (the sanitizer returns it unchanged, safely).
  return [name, address].filter(Boolean).join(', ');
}

interface EventLike {
  title?: string;
  location?: string;
  raw?: {
    caloneBaseTitle?: string;
    caloneLocationText?: string;
    [k: string]: any;
  } | null;
}

export function cleanEventTitleAndLocation(event: EventLike): { title: string; location: string } {
  let cleanTitle = event.title || '';
  if (event.raw?.caloneBaseTitle) {
    cleanTitle = event.raw.caloneBaseTitle;
  } else {
    const match = cleanTitle.match(/^(.*?)\s*\(\s*<a\b[^>]*>.*?<\/a>\s*\)\s*$/i);
    if (match) {
      cleanTitle = match[1].trim();
    } else {
      cleanTitle = cleanTitle.replace(/<[^>]*>/g, '').trim();
    }
  }

  const cleanLocation = event.raw?.caloneLocationText || labelFromLocationHtml(event.location || '');
  return { title: cleanTitle, location: cleanLocation };
}

export interface CalOneRawMetadata {
  caloneBaseTitle: string;
  caloneLocationText: string;
  caloneLocationSource: string;
  caloneLocationHtml: string;
  caloneTitleLocationHtml: string;
  caloneTitleLocationManaged: boolean;
}

export function formatEventOnSubmit(title: string, location: string): {
  displayTitle: string;
  locationHtml: string;
  raw: CalOneRawMetadata | null;
} {
  const cleanTitle = title.trim();
  const cleanLoc = location.trim();
  const hasUrl = LOCATION_URL_RE.test(cleanLoc);
  const locHtml = locationHtmlFromLabel(cleanLoc);
  const displayTitle = locHtml ? `${cleanTitle} (${locHtml})` : cleanTitle;
  const raw = locHtml ? {
    caloneBaseTitle: cleanTitle,
    caloneLocationText: cleanLoc,
    caloneLocationSource: hasUrl ? 'url' : 'google-maps',
    caloneLocationHtml: locHtml,
    caloneTitleLocationHtml: locHtml,
    caloneTitleLocationManaged: true,
  } : null;

  return { displayTitle, locationHtml: locHtml, raw };
}
